import i18next from "i18next";
import { SetStateAction, useEffect, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Link, Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import Header from "../components/Header";
import NavBar from "../components/NavBar";
import DownloadPage from "./DownloadPage";
import HomePage from "./HomePage";
import LicensePage from "./LicensePage";

import common_ar from "../../src/translations/ar/common.json";
import common_en from "../../src/translations/en/common.json";

i18next.init({
    interpolation: { escapeValue: false }, // React already does escaping
    lng: localStorage.getItem("selectedLanguage") || "en", // default language to use
    resources: {
        en: {
            common: common_en
        },
        ar: {
            common: common_ar
        }
    }
});

function LicensingApp({
    portalUrl = "https://www.arcgis.com/sharing/rest",
    productIds = [],
    subProductIds = [],
    licenseUrl,
    mediaUrl,
    cdnMediaUrl
}: {
    portalUrl: string;
    productIds: string[];
    subProductIds: any[];
    licenseUrl: string;
    mediaUrl: string;
    cdnMediaUrl?: string;
}) {
    // Translation
    const { t } = useTranslation("common");

    // Authentication
    const { token, isSignedIn, authUrlPath } = useAuth();

    const [productName, setProductName] = useState("");

    const handleProductName = (name: SetStateAction<string>) => {
        setProductName(name);
    };

    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        async function loadSelf() {
            try {
                if (isSignedIn) {
                    const self: any = await fetch(
                        `${portalUrl}/community/self?f=json&token=${token}`
                    ).then((rsp) => {
                        return rsp.json();
                    });
                    if (self.error !== undefined) throw new Error(self.error.message);
                    setUser(self);
                }
            } catch (syncErr) {
                console.log(syncErr); // TODO - relay this?
                setUser(null);
            }
        }
        loadSelf();
    }, []);

    return (
        <I18nextProvider i18n={i18next}>
            <Router basename="/">
                <div className="licensing-app">
                    <Header portalUrl={portalUrl} user={user} />
                    <Switch>
                        <Route path="/license/:productId/:productEditorsId/:productViewersId">
                            <>
                                <div className="app-body">
                                    <HomePage
                                        portalUrl={portalUrl}
                                        productIds={productIds}
                                        subProductIds={subProductIds}
                                        user={user}
                                    />
                                    <div
                                        className="fixed inset-0 overflow-y-auto"
                                        style={{ zIndex: 999 }}
                                    >
                                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                                            <div
                                                className="fixed inset-0 transition-opacity"
                                                aria-hidden="true"
                                            >
                                                <div className="absolute inset-0 bg-gray-800 opacity-75"></div>
                                            </div>
                                            <span
                                                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                                                aria-hidden="true"
                                            >
                                                &#8203;
                                            </span>
                                            <div
                                                className="inline-block align-bottom bg-white text-left overflow-hidden shadow-xl transform transition-all sm:my-10 sm:align-middle sm:max-w-3xl sm:w-full"
                                                role="dialog"
                                                aria-modal="true"
                                                aria-labelledby="modal-headline"
                                            >
                                                <div className="bg-white p-8">
                                                    <div className="font-medium leading-6 mb-6 modal-header relative text-gray-900 text-lg">
                                                        <>Generate license</>
                                                        <div className="-mr-4 absolute flex right-0 sm:pr-4 top-0">
                                                            <Link
                                                                to={token ? `/${authUrlPath}` : "/"}
                                                                className="rounded-md text-gray-300 hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
                                                            >
                                                                <svg
                                                                    className="h-6 w-6"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                    aria-hidden="true"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M6 18L18 6M6 6l12 12"
                                                                    />
                                                                </svg>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                    <div className="modal-body">
                                                        <LicensePage
                                                            portalUrl={portalUrl}
                                                            licenseUrl={licenseUrl}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        </Route>
                        <Route path="/resources/:productId">
                            <>
                                <NavBar productName={productName} />
                                <div className="app-body">
                                    <DownloadPage
                                        portalUrl={portalUrl}
                                        mediaUrl={mediaUrl}
                                        onProductNameRetrieved={handleProductName}
                                    />
                                </div>
                            </>
                        </Route>
                        <Route path="/downloads/sweet-offline-windows">
                            <>
                                {/* <AppDownloadsHeader
                                    portalUrl={portalUrl}
                                    user={user}
                                    appTitle={"Sweet for ArcGIS - Offline - Windows"}
                                /> */}
                                <NavBar productName={productName} />
                                <div className="app-body">
                                    <DownloadPage
                                        portalUrl={portalUrl}
                                        mediaUrl={mediaUrl}
                                        product={{
                                            id: "sweet-offline-windows",
                                            title: "Sweet for ArcGIS - Offline - Windows",
                                            thumbnail:
                                                "https://www.arcgis.com/sharing/rest/content/listings/1cf5a9c65fed4beb882b628470ddbe8b/info/screenshots/screenshot_1602678950687.png",
                                            url: "https://help.sweet.esriuk.com/download-the-app/",
                                            anonymous: true,
                                            single: true,
                                            mediaUrl:
                                                cdnMediaUrl ||
                                                "https://sweetpipelinestorage.blob.core.windows.net/esriuk-product-media",
                                            downloadPath: "/{filename}"
                                        }}
                                        onProductNameRetrieved={handleProductName}
                                    />
                                </div>
                            </>
                        </Route>
                        <Route path="/">
                            <>
                                <div className="app-body">
                                    <HomePage
                                        portalUrl={portalUrl}
                                        productIds={productIds}
                                        subProductIds={subProductIds}
                                        user={user}
                                    />
                                </div>
                            </>
                        </Route>
                    </Switch>
                </div>
            </Router>
        </I18nextProvider>
    );
}

export default LicensingApp;
